import React, { useMemo } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import TaskAvatar from '@/components/Tasks/TaskAvatar';
import TaskAttachment from '@/components/Tasks/TaskAttachment';
import TaskFavoriteHeart from '@/components/Tasks/TaskFavoriteHeart';

const createStyles = (colors, isXS, isAltBGColor) => StyleSheet.create({
  card: {
    borderRadius: 8,
    padding: isXS ? 10 : 15,
    backgroundColor: isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block,
    borderWidth: isXS ? 0 : 1,
    borderColor: isXS ? 'transparent' : colors.border_color_secondary,
    flexDirection: 'column',
    minHeight: isXS ? 60 : 52,
  },
  cardHovered: {
    backgroundColor: isAltBGColor ? colors.solid_color_secondary_dark : colors.solid_color_tertiary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerLeft: {
    flex: 1,
    minWidth: 0,
    flexDirection: isXS ? 'row-reverse' : 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: 4,
  },
  statusIconWrapper: {
    width: 20,
    height: 20,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    marginLeft: isXS ? 'auto' : 0,
  },
  name: {
    flexShrink: 1,
  },
  headerMeta: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  metaRow: {
    marginTop: 4,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
});

function formatTime(time) {
  if (!time) return '';
  if (typeof time === 'string') return time;
  const { hour, minute } = time;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  const m = String(minute).padStart(2, '0');
  return `${h}:${m} ${suffix}`;
}

function toOrdinal(day) {
  const n = parseInt(day, 10);
  if (!Number.isFinite(n)) return day;
  const mod100 = n % 100;
  let suffix = 'th';
  if (mod100 < 11 || mod100 > 13) {
    const mod10 = n % 10;
    if (mod10 === 1) suffix = 'st';
    else if (mod10 === 2) suffix = 'nd';
    else if (mod10 === 3) suffix = 'rd';
  }
  return `${n}${suffix}`;
}

function formatSchedule(schedule) {
  if (!schedule || !schedule.type) return 'Once';

  const type = schedule.type;
  const time = formatTime(schedule.timeOfDay);
  const dayOfWeek = Number.isInteger(schedule.daysOfWeek)
    ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][schedule.daysOfWeek]
    : schedule.dayOfWeek;
  const dayOfMonth = schedule.dayOfMonth;
  const month = schedule.month;

  if (type === 'daily') {
    return time ? `Daily at ${time}` : 'Daily';
  }

  if (type === 'weekly') {
    const base =
      dayOfWeek && typeof dayOfWeek === 'string'
        ? dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1)
        : 'Weekly';
    const plural =
      base.endsWith('s') || base.toLowerCase() === 'weekly'
        ? base
        : `${base}s`;
    return time ? `${plural} at ${time}` : plural;
  }

  if (type === 'monthly') {
    const dayLabel = dayOfMonth ? toOrdinal(dayOfMonth) : null;
    if (dayLabel) {
      return time
        ? `Monthly on the ${dayLabel} at ${time}`
        : `Monthly on the ${dayLabel}`;
    }
    return 'Monthly';
  }

  if (type === 'annually') {
    let monthLabel = month
      ? month.charAt(0).toUpperCase() + month.slice(1)
      : null;
    const dayLabel = dayOfMonth ? toOrdinal(dayOfMonth) : null;
    if (monthLabel && dayLabel) {
      return time
        ? `${monthLabel} ${dayLabel} at ${time}`
        : `${monthLabel} ${dayLabel}`;
    }
    return 'Annually';
  }

  return type.charAt(0).toUpperCase() + type.slice(1);
}

function InferenceTaskListItem({ task, onPress }) {
  const { colors, theme } = useTheme();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isXS, isAltBGColor), [colors, isXS, isAltBGColor]);

  const enabled = task?.flags?.isEnabled ?? true;
  const scheduleLabel = formatSchedule(task?.schedule);
  const isOnce = !task?.schedule || task?.schedule?.type === 'once';
  const runAt =
    task?.schedule?.runAt != null ? new Date(task.schedule.runAt) : null;
  const runAtLabel = runAt
    ? runAt.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  return (
    <Pressable
      style={({ hovered }) => [
        styles.card,
        hovered && styles.cardHovered,
      ]}
      onPress={() => onPress?.(task)}
    >
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {!!task?.agentId && <TaskAvatar task={task} size={32} />}
          <View style={styles.statusIconWrapper}>
            <Icon
              name={enabled ? 'circle-check' : 'circle-xmark'}
              size={16}
              color={
                enabled
                  ? colors.accent_color_primary
                  : colors.text_color_secondary
              }
              solid
            />
          </View>
          <Text
            size="n"
            weight="medium"
            style={styles.name}
            numberOfLines={1}
          >
            {task?.name || 'Untitled task'}
          </Text>
        </View>

        <View style={styles.headerRight}>
          {!isXS && !isOnce && (
            <View style={styles.headerMeta}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <Icon
                  name="arrows-rotate"
                  size={13}
                  color={colors.text_color_secondary}
                  solid
                />
                <Text size="xs" color="secondary" numberOfLines={1}>
                  {scheduleLabel}
                </Text>
              </View>
            </View>
          )}

          {!isXS && isOnce && runAtLabel && (
            <View style={styles.headerMeta}>
              <Text size="xs" color="secondary" numberOfLines={1}>
                {runAtLabel}
              </Text>
            </View>
          )}

          <TaskFavoriteHeart task={task} />
        </View>
      </View>

      <TaskAttachment task={task} />

      {isXS && !isOnce && (
        <View style={styles.metaRow}>
          <Icon
            name="arrows-rotate"
            size={12}
            color={colors.text_color_secondary}
            solid
            style={{ marginRight: 4 }}
          />
          <Text size="xs" color="secondary">
            {scheduleLabel}
          </Text>
        </View>
      )}

      {isXS && isOnce && runAtLabel && (
        <View style={styles.metaRow}>
          <Text size="xs" color="secondary">
            {runAtLabel}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

export default React.memo(InferenceTaskListItem);


